/**
 * Human description of a selection, for the back control (REQ-UI-186).
 *
 * The control names what it will return to — "‹ Back to track TRK-ZC1JAB"
 * — because a bare ``‹`` makes the operator click to find out where it
 * goes, and on a surface where one wrong step costs a re-run query that
 * is a bad trade (CLAUDE.md §8.1, operator visibility).
 *
 * The one rule that is not cosmetic: a **Query-reached entity describes
 * itself by what it is**. The bus carries such a selection as
 * ``query-record``, but the pane renders track (or detection, or alert)
 * sections, so "back to track" is what the operator is returning to;
 * "back to record" would name the plumbing instead of the thing.
 *
 * Cross-references:
 *   - REQ-UI-186 (the back control), REQ-UI-183 (why a query record can
 *     *be* an entity).
 */
import { entityKindForRecordType } from "@/entity-details/entity-record-mapping";
import { shortId } from "@/lib/short-id";
import type { SelectionValue } from "@/selection/selection-store";

/** Short-handle prefix per entity kind, for the kinds whose ids are ULIDs. */
const HANDLE_PREFIX = {
  detection: "DET",
  alert: "ALT",
  track: "TRK",
} as const;

/** Characters of a producer UUID shown as its handle — the panel subtitle's. */
const UUID_HANDLE_CHARS = 8;

/**
 * Describe ``value`` as a short noun phrase: the kind, then a handle the
 * operator has seen elsewhere in the UI.
 *
 * @param value - Any selection the bus can hold.
 * @returns e.g. ``"track TRK-ZC1JAB"``, ``"platform 8f3a1b2c"``,
 *   ``"sapient_status record"``.
 */
export function describeSelection(value: SelectionValue): string {
  switch (value.kind) {
    case "detection":
    case "alert":
    case "track":
      return `${value.kind} ${shortId(value.id, HANDLE_PREFIX[value.kind])}`;
    case "platform":
    case "aggregator":
      // Producer ids are UUIDs, not ULIDs: a synthetic ``PLT-`` handle would
      // be an identifier that appears nowhere else. The leading octet is what
      // the pane's own subtitle shows.
      return `${value.kind} ${value.id.slice(0, UUID_HANDLE_CHARS)}`;
    case "query-record": {
      const kind = entityKindForRecordType(value.recordType);
      // An entity-backed record describes itself as the entity — the pane the
      // operator will land on renders that entity's sections.
      return kind === null
        ? `${value.recordType} record`
        : describeSelection({ kind, id: value.id });
    }
    case "status-component":
      return `component ${value.id}`;
    case "status-peer":
      return `peer ${value.peerName}`;
    case "status-interface":
      return `interface ${value.interfaceName}`;
    case "status-connection":
      return `connection ${value.id}`;
  }
}
